import React, { useCallback, useMemo } from 'react';
import DebouncedAutoComplete from './DebouncedAutoComplete';
import { useFetchPostalCodeSuggestions } from '../../hooks/useFetchPostalCodeSuggestions';
import { FormikProps } from 'formik';
import { FormikTextField } from './FormikTextField';
import { useTranslation } from 'react-i18next';

type RequiredFormikValues = {
  address: {
    postalCode?: string;
  };
};

type PostalCodeInputProps<T extends RequiredFormikValues> = {
  formik: FormikProps<T>;
  label: string;
  value?: string;
  countryISO?: string;
  autoComplete: boolean;
};

export const PostalCodeInput = <T extends RequiredFormikValues>({
  label,
  value,
  countryISO,
  formik,
  autoComplete,
}: PostalCodeInputProps<T>) => {
  const fetchPostalCodeSuggestions = useFetchPostalCodeSuggestions({ countryISO, limit: 10 });
  const { touched, setTouched, values, setValues, errors } =
    formik as unknown as FormikProps<RequiredFormikValues>;

  const { t } = useTranslation();

  const validationMessage = useMemo(() => {
    const isTouched = touched.address?.postalCode || false;
    const errorMessage = errors.address?.postalCode || '';
    if (isTouched && errorMessage) {
      return t(errorMessage);
    }
    return undefined;
  }, [touched.address?.postalCode, errors.address?.postalCode, t]);

  const onChange = useCallback(
    (value: string) =>
      setValues((prevState) => {
        return { ...prevState, address: { ...values.address, postalCode: value } };
      }),
    [setValues, values.address]
  );

  const onBlur = useCallback(() => {
    if (!touched.address?.postalCode) {
      setTouched({ ...touched, address: { ...touched.address, postalCode: true } });
    }
  }, [touched, setTouched]);

  return (
    <>
      {autoComplete ? (
        <DebouncedAutoComplete
          label={label}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          validationMessage={validationMessage}
          fetchSuggestions={fetchPostalCodeSuggestions}
          dataCy="addressPostalCode"
        />
      ) : (
        <FormikTextField
          value={value ?? ''}
          name={'address.postalCode'}
          id="address.postalCode"
          formikProps={formik}
          label={t('register.address.postalCode')}
          data-cy="addressPostalCode"
        />
      )}
    </>
  );
};
